import { useTranslation } from 'react-i18next';

import { describeApiError } from '@/domain/shared/describeApiError';

import styles from './ApiErrorMessage.module.scss';

interface ApiErrorMessageProps {
    /** Whatever the failed request rejected with. */
    error: unknown;
    /** Translated copy naming what failed; the generic message is used without it. */
    fallback?: string;
    /** Wraps the server's reason for a refusal in the caller's own sentence. */
    formatRefusal?: (message: string) => string;
}

/**
 * The text of a failed request, for an error toast or an inline form error. A refusal (4xx)
 * shows the server's reason, which is written for the user. A server failure (5xx) shows
 * translated copy, since its text is meant for the log, plus the request id the backend logged
 * it under so support can find it. A lost connection says so.
 */
export function ApiErrorMessage(props: ApiErrorMessageProps) {
    const { error, fallback, formatRefusal } = props;
    const { t } = useTranslation();
    const described = describeApiError(error);
    const generic = fallback ?? t('common.errors.somethingWentWrong');

    switch (described.kind) {
        case 'client':
            if (described.message === undefined) return <>{generic}</>;
            return <>{formatRefusal ? formatRefusal(described.message) : described.message}</>;
        case 'network':
            return <>{t('common.errors.network')}</>;
        case 'server':
            return (
                <>
                    {fallback ?? t('common.errors.server')}
                    {described.requestId && (
                        <small className={styles.reference}>
                            {t('common.errors.reference', { requestId: described.requestId })}
                        </small>
                    )}
                </>
            );
        default:
            return <>{generic}</>;
    }
}
